import React, { useState, useEffect } from "react";
import "./TaxProPayments.scss";
import { Paper, Skeleton } from "@mantine/core";
import { useCreateStripeIntegrationOnClick } from "@/utils/hooks/usePayments";
import PaymentWelcome from "./PaymentWelcome/PaymentWelcome";
import { useStripeOnboardingStatus } from "@/utils/hooks/usePaymentOnboardingStatus";
import PaymentTable from "./PaymentTable/PaymentTable";
import PaymentButtons from "./PaymentButtons/PaymentButtons";

const TaxProPayments: React.FC = () => {
  // GET hook: Runs on mount to fetch onboarding status.
  const {
    data: onboardingData,
    isLoading: isStatusLoading,
    error: statusError,
    refetch: refetchOnboardingStatus,
  } = useStripeOnboardingStatus();

  // POST hook: Only runs when triggered.
  const {
    isPending: isCreateLoading,
    isError: isCreateError,
    error: createError,
    mutateAsync: createIntegration,
  } = useCreateStripeIntegrationOnClick();

  if (statusError) {
    console.error("Error fetching onboarding status:", statusError);
  }

  // Check if any onboarding data exists (meaning they've started onboarding)
  const hasStartedOnboarding = Boolean(onboardingData?.content);

  // Check if the onboarding was completed (a true flag from your API)
  const onboardingCompleted = onboardingData?.content?.onboardingCompleted;

  const handleCreateStripeIntegration = async () => {
    try {
      // Build the return URL dynamically based on current origin
      const returnUrl = `${window.location.origin}/payments`;
      const result = await createIntegration(returnUrl);
      // If the API returns a URL (for creating a new integration), redirect to it.
      const url = result?.content?.url;
      if (url) {
        // Redirect instead of opening a new tab
        window.location.href = url;
      }
    } catch (err) {
      console.error("Error creating Stripe integration:", err);
    }
  };

  // Add a little timeout to ensure the skeleton is shown for at least 300ms.
  const [minLoadingPassed, setMinLoadingPassed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinLoadingPassed(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Force a refetch on mount in case the user is coming back from Stripe.
  useEffect(() => {
    refetchOnboardingStatus();
  }, [refetchOnboardingStatus]);

  const showSkeleton = isStatusLoading || !minLoadingPassed;

  // Determine which component to render based on the state
  let paymentContent;
  if (showSkeleton) {
    paymentContent = <Skeleton height={200} />;
  } else if (onboardingCompleted) {
    paymentContent = <PaymentTable />;
  } else {
    paymentContent = (
      <PaymentWelcome
        handleCreateStripeIntegration={handleCreateStripeIntegration}
        isLoading={isCreateLoading}
        isError={isCreateError}
        error={createError}
        isInProgresson={hasStartedOnboarding}
      />
    );
  }

  return (
    <div className="tax-pro-payments">
      {onboardingCompleted && <PaymentButtons />}
      <Paper shadow="xs" radius="md" p="xl">
        <div className="tax-pro-payments-container">{paymentContent}</div>
      </Paper>
    </div>
  );
};

export default TaxProPayments;
